import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input,
  OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { animate, AnimationEvent, group, query, state, style, transition, trigger } from '@angular/animations';
import {
  MINIMUM_MODAL_HEIGHT, MINIMUM_MODAL_WIDTH, ModalButtonsLabels, ModalConfig, ModalSizes,
  modalSizes, ModalSizesDefinition, ModalSizesKeys, ModalTypes, modalTypes, ModalTypesDefinition
} from './modal.types';
import { BoxModelService, EventsService, IconsOld } from '@circe/core';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, delay, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'npa-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('modalFade', [
      state('void, hidden, autoClose', style({ opacity: 0 })),
      state('visible', style({ opacity: 1 })),
      transition('* => visible', [
        query('@disappear', [
          animate(0, style({ opacity: 1 }))
        ]),
        animate('250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)', style({ opacity: 1 }))
      ]),
      transition('visible => hidden, visible => autoClose', [
        query('@disappear', [
          animate(0, style({ opacity: 0 }))
        ]),
        group([
          query('@modalBox', [
            group([
              animate('300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)', style({ transform: 'translateY(-20px)' })),
              animate('300ms linear', style({ opacity: 0 }))
            ])
          ]),
          animate('300ms linear', style({ opacity: 0 }))
        ])
      ])
    ]),
    trigger('disappear', []),
    trigger('modalBox', [])
  ]
})
export class ModalComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('modalContainer', { static: false }) modalContainer: ElementRef;
  @ViewChild('modal', { static: false }) modal: ElementRef;
  @ViewChild('modalBody', { static: false }) modalBody: ElementRef;

  @Input() title: string;
  @Input() buttonsLabels: ModalButtonsLabels;
  @Input() hotRender: boolean;
  @Input() disabled: boolean;
  @Input() modalConfig: ModalConfig;
  @Input()
  get showModal(): boolean {
    return this.showModalHTML;
  }
  set showModal(showModal: boolean) {
    if (showModal) {
      this.showModalHTML = true;
      if (this.modalConfig) {
        this.visibility = 'visible';
        this._cd.detectChanges();
        this._processConfiguration();

        this._componentDestroyed$.next();
        fromEvent(this.modalBody.nativeElement, 'scroll').pipe(
          takeUntil(this._componentDestroyed$),
          debounceTime(10),
          delay(1)
        ).subscribe(() => {
          this._cd.detectChanges();
        });
      }
    } else {
      this.visibility = 'hidden';
    }
  }

  @Output() closeModal: EventEmitter<void>;
  @Output() cancel: EventEmitter<void>;
  @Output() action: EventEmitter<void>;

  public showModalHTML: boolean;
  public visibility: string;
  public modalType: ModalTypes;
  public modalSize: ModalSizes;
  public modalTypes: ModalTypesDefinition;
  public modalSizes: ModalSizesDefinition;
  public isCloseControl: boolean;
  public closeControlIcon: string;
  public statusIcon: string;
  public allowClickOutside: boolean;
  public isFullWindow: boolean;
  public enabledAnimation: boolean;
  public overlay: boolean;
  public onlyActionButton: boolean;
  public showDefaultHeader: boolean;
  public fullWindowHeaderIcon: string;

  private _host: Element;
  private _iconHandler: IconsOld;
  private _configIsSet: boolean;
  private _width: string;
  private _height: string;
  private _elementReference: Element;
  private _elementReferenceRect: DOMRect;
  private _modalElement: Element;
  private _modalElementRect: DOMRect;
  private _fullWindowHeaderIconHandler: IconsOld;
  private readonly ESCAPE_KEYCODE: string = 'Escape';
  private readonly _componentDestroyed$: Subject<void> = new Subject<void>();

  @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent): void {
    if (event.key === this.ESCAPE_KEYCODE) {
      this.closeModal.emit();
    }
  }

  constructor(
    public ev: EventsService,
    private _bm: BoxModelService,
    private _cd: ChangeDetectorRef,
    private _el: ElementRef<Element>
  ) {
    this.modalTypes = modalTypes;
    this.modalSizes = modalSizes;
    this._host = _el.nativeElement;
    this._elementReference = this._bm.getElement({ name: 'html', type: 'tag' });
    this._elementReferenceRect = this._elementReference.getBoundingClientRect();
    this.visibility = 'visible';
    this.showModalHTML = false;
    this.hotRender = false;
    this.disabled = false;
    this.closeModal = new EventEmitter();
    this.cancel = new EventEmitter();
    this.action = new EventEmitter();
  }

  public ngOnInit(): void {
    if (this.modalConfig) {
      this._processConfiguration();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.hotRender && changes.modalConfig && changes.modalConfig.currentValue) {
      this._processConfiguration();
    }
  }

  public animationDone(event: AnimationEvent): void {
    if (!!this.modalContainer?.nativeElement) {
      (this.modalContainer.nativeElement as HTMLElement).focus();
    }
    if (event.toState === 'hidden') {
      this.showModalHTML = false;
    }
  }

  public onClickOutside(event: MouseEvent): void {
    if (event.target === this.modalContainer.nativeElement && this.allowClickOutside) {
      this.closeModal.emit();
    }
  }

  private _processSize(): void {
    let minHeight: string = (this.modalType === 'custom' ? MINIMUM_MODAL_HEIGHT : 280) + 'px';
    let minWidth: string = MINIMUM_MODAL_WIDTH + 'px';
    if (this._configIsSet) {
      this._height = '';
      this._width = '';
      if (this.modalConfig.fullWindow) {
        this._height = '100vh';
        this._width = '100vw';
      } else if (this.modalConfig.size && this.modalConfig.size === ModalSizesKeys.custom) {
        this._height = this.modalConfig.height ? this.modalConfig.height + 'px' : 'fit-content';
        this._width = this.modalConfig.width ? this.modalConfig.width + 'px' : 'fit-content';
        minHeight = '0px';
        minWidth = '0px';
      }

      const _modalElement: Element = this._host.getElementsByClassName('npa-modal__box').item(0);
      this._modalElement = _modalElement;

      if (!!this._modalElement) {
        this._modalElementRect = this._modalElement.getBoundingClientRect();
        (_modalElement as HTMLElement).style.height = this._height;
        (_modalElement as HTMLElement).style.width = this._width;
        (_modalElement as HTMLElement).style.minHeight = minHeight;
        (_modalElement as HTMLElement).style.minWidth = minWidth;
        (_modalElement as HTMLElement).style.maxHeight = (this.isFullWindow ? '100vh' : (window.innerHeight * 0.7)) + 'px';
        (_modalElement as HTMLElement).style.maxWidth = this.isFullWindow ? 'unset' : window.innerWidth + 'px';
      }
    }
  }

  private _processConfiguration(config: ModalConfig = this.modalConfig): void {
    this._configIsSet = (!!config);

    this.modalType = modalTypes.info;
    if (this._configIsSet && 'type' in config) {
      this.modalType = config.type;
    }

    this.modalSize = ModalSizesKeys.regular;
    if (this._configIsSet && this.modalType === 'custom' && 'size' in config) {
      this.modalSize = config.size;
    }

    this._iconHandler = new IconsOld();
    if (this._configIsSet && 'iconClassBase' in config) {
      this._iconHandler = new IconsOld(config.iconClassBase);
    }

    this.isCloseControl = true;
    if (this._configIsSet && 'closeControl' in config) {
      this.isCloseControl = config.closeControl;
    }

    this.closeControlIcon = this._iconHandler.get(this._iconHandler.defaultKeys.close);
    if (this._configIsSet && !this.isCloseControl) {
      this.closeControlIcon = '';
    }

    this.statusIcon = '';
    if (this._configIsSet && (config.type === modalTypes.warning || config.type === modalTypes.critical)) {
      this.statusIcon = this._iconHandler.get(config.type);
    }

    this.allowClickOutside = true;
    if (this._configIsSet && 'clickOutside' in config) {
      this.allowClickOutside = config.clickOutside;
    }

    this.showDefaultHeader = true;
    if (this._configIsSet && 'showDefaultHeader' in config) {
      this.showDefaultHeader = config.showDefaultHeader;
    }

    this.isFullWindow = false;
    if (this._configIsSet && 'fullWindow' in config) {
      this.isFullWindow = config.fullWindow;
    }

    this.enabledAnimation = true;
    if (this._configIsSet && 'enableAnimation' in config) {
      this.enabledAnimation = config.enableAnimation;
    }

    this.overlay = true;
    if (this._configIsSet && 'overlay' in config) {
      this.overlay = config.overlay;
    }

    this.onlyActionButton = false;
    if (this._configIsSet && 'onlyActionButton' in config) {
      this.onlyActionButton = config.onlyActionButton;
    }

    this._fullWindowHeaderIconHandler = new IconsOld();
    let fullWindowHeaderIcon: string = this._fullWindowHeaderIconHandler.classBase;
    if (this._configIsSet && 'fullWindowHeaderIcon' in config) {
      if (typeof config.fullWindowHeaderIcon === 'boolean') {
        fullWindowHeaderIcon = config.fullWindowHeaderIcon ? fullWindowHeaderIcon : '';
      } else if (typeof config.fullWindowHeaderIcon === 'object') {
        this._fullWindowHeaderIconHandler = new IconsOld(config.iconClassBase);
        fullWindowHeaderIcon = this._fullWindowHeaderIconHandler.getIcon(config.fullWindowHeaderIcon);
      }
    } else {
      this.fullWindowHeaderIcon = '';
    }
    this.fullWindowHeaderIcon = fullWindowHeaderIcon;

    this._cd.detectChanges();

    if (!this.disabled) {
      this._processSize();
    }
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
    this._componentDestroyed$.complete();
    this._componentDestroyed$.unsubscribe();
  }
}
